import React, { Suspense } from 'react';
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IonApp, IonSpinner, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { InitAuth } from './services/authService';
import { InitPush } from './services/pushService';
import { InitDevice } from './services/deviceService';
import { InitUtm } from './services/utmService';
import { initPosthog } from './services/posthogService';

// Bootstrap PostHog before any auth/identify code runs. No-op if
// VITE_POSTHOG_KEY is unset (dev default). Calling at module top
// (not inside a component) so the SDK's pageview-on-init captures
// the actual landing URL with any UTM/promo params present —
// rendering-time init would race the initial route change.
initPosthog('core');
import { InitLocale } from './services/localeInit';
import { DeviceLocationGuard } from './services/deviceLocationGuard';
import { InitWs, setWsQueryClient } from './services/wsService';
import { setNoNetworkNavigator, setServiceUnavailableNavigator } from './services/apiService';
import {
  ACCOUNT_STATUS_PATH,
  setAccountStatusNavigator,
} from './services/accountStatusService';
import { YbugProvider } from 'ybug-react';
import { InitYbug } from './services/ybugInit';
import { isIOS, ybugSettings } from './services/ybugSettings';
import { useAuthStore } from './store/useAuthStore';
import { isWithinShareArrivalGrace } from './services/authStorageKeys';
import { useProfileStore } from './store/useProfileStore';
import { useEffect } from 'react';
import { logInfo } from './services/loggerService';
import { useAppBadge } from './hooks/useAppBadge';

import { Toaster } from 'react-hot-toast';
import { HomePage } from './pages/HomePage';
import { GlobalErrorModal } from './components/ui/GlobalErrorModal';
import { ExpiredLinkModal } from './components/ui/ExpiredLinkModal';
import { PushBlockedModal } from './components/ui/PushBlockedModal';
import { UpdateAvailableBanner } from './components/ui/UpdateAvailableBanner';
import { NavigationTracker } from './components/ui/NavigationTracker';
import { AuthGuard } from './components/auth/AuthGuard';
import { SecretLoginHandler } from './components/auth/SecretLoginHandler';
import { PushPermissionModal } from './components/auth/PushPermissionModal';
import { PushFirstLaunchTrigger } from './components/auth/PushFirstLaunchTrigger';
import { InstallInstructionsModal } from './components/ui/InstallInstructionsModal';
import { PushTapNavigator } from './components/auth/PushTapNavigator';
import { PwaUpdateInitializer } from './components/auth/PwaUpdateInitializer';
import { VersionCheckRunner } from './components/auth/VersionCheckRunner';
import { PublishListingPage } from './pages/PublishListingPage';
import { ListingDetailsPage } from './pages/ListingDetailsPage';
import { OrderCheckoutPage } from './pages/OrderCheckoutPage';
import { OrderInProgressPage } from './pages/OrderInProgressPage';
import { ShareRedirectPage } from './pages/ShareRedirectPage';
import { NotificationRefPage } from './pages/NotificationRefPage';
import { CampaignLinkPage } from './pages/CampaignLinkPage';
import { ConnectionResolvePage } from './pages/ConnectionResolvePage';
import { UpdateRequiredPage } from './pages/UpdateRequiredPage';
import { AccountStatusPage } from './pages/AccountStatusPage';
import { NoNetworkPage } from './pages/NoNetworkPage';
import { ServiceUnavailableScreen } from './components/ui/ServiceUnavailableScreen';
import { RegistrationRequiredPage } from './pages/RegistrationRequiredPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsNotificationsPage } from './pages/SettingsNotificationsPage';
import { ConnectionsVisibilityPage } from './pages/ConnectionsVisibilityPage';
import { MyKitchenPage } from './pages/MyKitchenPage';
import { MyEarningsPage } from './pages/MyEarningsPage';
import { MyOrdersPage } from './pages/MyOrdersPage';
import { ProfileMenu } from './components/ui/ProfileMenu';
import { GuestMenu } from './components/ui/GuestMenu';
import { ActivityListModal } from './components/activity/ActivityListModal';
import { OrderConfirmGate } from './components/orders/OrderConfirmGate';
import { useActivitySheetStore } from './store/useActivitySheetStore';
import { TextScaleDevTool } from './components/dev/TextScaleDevTool';

import './styles/global.scss';

setupIonicReact({ mode: 'md' });

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // Optimistic paint: `isAuthenticated` is hydrated synchronously at store
  // creation from the auth sentinel (csrf_token cookie / JWT). The old
  // `isLoading` short-circuit is gone since `isLoading` is always false now
  // (whoami runs in the background; cached state is the optimistic truth).
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { search } = useLocation();
  // If we're mid-secret-login (?s=<secret> on the URL), the user is not
  // authenticated YET but SecretLoginHandler is about to mint a JWT and
  // strip the param. Rendering <Redirect to="/" /> here would flash the
  // home page before the handshake completes and would also lose the
  // ?s= we need to consume. Show nothing for the brief moment instead.
  if (!isAuthenticated) {
    if (hasSecretLoginParam(search)) return null;
    return <Redirect to="/" />;
  }
  return <AuthGuard>{children}</AuthGuard>;
}

// NOTE: `/update-required` is exempt from ProtectedRoute too — see the
// individual gates below for the same pattern. It's a terminal recovery
// page that must work for unauthenticated users.

/**
 * Returns true when the URL carries a secret-login token (`?s=<secret>`).
 * Such URLs are in the middle of an auto-login handshake — every app gate
 * (location, registration, onboarding) must let them through so the secret
 * can be processed before any redirect kicks in. Once secret-login completes
 * it strips the `?s=` param, after which the gates fire normally against the
 * clean URL.
 */
function hasSecretLoginParam(search: string): boolean {
  return new URLSearchParams(search).has('s');
}

/**
 * Returns true when the URL is the notification shortlink resolver
 * (`/n/<ref>`, typically arrived at via an SMS tap). Like the secret-login
 * URLs above, this is a transient handshake — the resolver page fetches the
 * underlying NotificationSummary and replaces the URL with the destination.
 * Every gate must let it through so that handshake can complete.
 */
function isNotificationRefPath(pathname: string): boolean {
  return pathname.startsWith('/n/');
}

/**
 * Returns true for a campaign short link (`/r/<ref>`, from a shared link or a
 * printed QR code). Same transient handshake as `/n/<ref>`: the page resolves
 * the ref and does a full navigation to the landing URL, so every gate must
 * let it through — the visitor is usually signed out and new.
 */
function isCampaignLinkPath(pathname: string): boolean {
  return pathname.startsWith('/r/');
}

/**
 * Returns true when the URL is the terminal `Update Required` recovery page.
 * Surfaced by the /ping minimum-version lifeline when an automatic nuclear
 * reset has already been attempted this session and the FE is still below
 * the BE's supported version. This page must be reachable regardless of
 * auth/location/registration/onboarding state — it's the user's only way
 * back to a working app.
 */
function isUpdateRequiredPath(pathname: string): boolean {
  return pathname === '/update-required';
}

/**
 * Returns true when the URL is the terminal `No Network` recovery page.
 * Surfaced by apiService when a fetch throws because the network is
 * unavailable. Must be reachable regardless of auth/location/registration/
 * onboarding state — no gate can pass without network.
 */
function isNoNetworkPath(pathname: string): boolean {
  return pathname === '/no-network';
}

/**
 * Returns true when the URL is the terminal `Service Unavailable` recovery
 * page. Surfaced by apiService on a 503 (the backend started without a DB —
 * "degraded mode", e.g. staging's DB stopped to save cost, or a real outage).
 * Must be reachable regardless of auth/location/registration state — no gate
 * can pass while the backend can't serve data.
 */
function isServiceUnavailablePath(pathname: string): boolean {
  return pathname === '/service-unavailable';
}

/**
 * Returns true when the URL is the terminal `Account Status` wall, surfaced
 * when whoami resolves a profile whose status is inactive/suspended/removed/
 * deleted. Must be reachable regardless of registration/onboarding state —
 * the user IS authenticated (we keep the session on purpose), they just have
 * nowhere else to go, and every gate must let them sit here.
 */
function isAccountStatusPath(pathname: string): boolean {
  return pathname === ACCOUNT_STATUS_PATH;
}

/**
 * Returns true when the URL is the connection deep link (`/c=<id>`). Like the
 * share-redirect landing, an invitee must reach this page first — before the
 * location/registration/onboarding gates fire — so they can see (and sign in
 * to respond to) the request. The gates still apply once they navigate on.
 */
function isConnectionResolvePath(pathname: string): boolean {
  return pathname.startsWith('/c=');
}

/**
 * Gate that catches authenticated users whose profile is missing required
 * registration fields (firstName, lastName, displayName). This happens when a
 * user lands via the access-code path: /exists creates a pending profile and
 * /login-code authenticates them, but /register is never called. Sends them
 * through a finish-registration page that calls /register with whatever fields
 * they're missing.
 */
const REGISTRATION_GATE_EXEMPT_PATHS = new Set<string>([
  '/registration-required',
  '/share',
]);

function RegistrationGate({ children }: { children: React.ReactNode }) {
  // Optimistic paint: read the cached `isRegistered` boolean rather than
  // computing from `activeProfile` (which may still be null while whoami
  // is in flight). The cache is rewritten at the end of every successful
  // whoami, so the gate re-evaluates with fresh truth ~1 RTT after boot.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isRegistered = useAuthStore((s) => s.isRegistered);
  const { pathname, search } = useLocation();

  if (!isAuthenticated) {
    if (pathname === '/registration-required') return <Redirect to="/" />;
    return <>{children}</>;
  }

  const exempt =
    REGISTRATION_GATE_EXEMPT_PATHS.has(pathname) ||
    hasSecretLoginParam(search) ||
    isNotificationRefPath(pathname) ||
    isCampaignLinkPath(pathname) ||
    isConnectionResolvePath(pathname) ||
    isUpdateRequiredPath(pathname) ||
    isNoNetworkPath(pathname) ||
    isServiceUnavailablePath(pathname) ||
    isAccountStatusPath(pathname);

  // Share-arrival grace: a visitor who just clicked a share link with a
  // stale `csrf_token` cookie but a missing `hp_gates` cache (cleared on
  // a prior logout) lands with `isAuthenticated=true` + `isRegistered=false`.
  // Without this gate, they'd be bounced through /registration-required
  // mid-share-flow. The flag is stamped by `ShareRedirectPage` on mount and
  // honored for 30s — only the bootstrap window. Later legitimate
  // unregistered-authed states (e.g. /login-code visitor finishing onboarding)
  // happen well outside the window and still hit the redirect.
  if (!isRegistered && !exempt && !isWithinShareArrivalGrace()) {
    const returnTo = encodeURIComponent(pathname + search);
    return <Redirect to={`/registration-required?returnTo=${returnTo}`} />;
  }
  if (isRegistered && pathname === '/registration-required') {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

/**
 * Activity pull-up sheet, mounted at the App root (not inside the per-page
 * TopBar) so it has a stable position that parent re-renders/navigation can't
 * remount. The bell in TopBar opens it via `useActivitySheetStore`.
 */
function ActivitySheetMount() {
  const isOpen = useActivitySheetStore((s) => s.isOpen);
  const close = useActivitySheetStore((s) => s.close);
  return <ActivityListModal isOpen={isOpen} onClose={close} />;
}

/**
 * Two-part network watcher:
 *   1. Registers a client-side navigator with apiService so fetch failures can
 *      route into `/no-network` without a full reload (a full reload can't
 *      fetch the SPA shell while offline — no service worker in dev).
 *   2. Proactively listens to the browser `offline` event and bounces straight
 *      to `/no-network` the moment the network drops, instead of waiting for
 *      a fetch to fail. Also catches the cold-load case where the app first
 *      loads with `navigator.onLine === false`.
 *
 * Must be mounted inside IonReactRouter so useHistory() is available.
 */
function NoNetworkNavigatorRegistration() {
  const history = useHistory();
  useEffect(() => {
    const goNoNetwork = () => {
      if (window.location.pathname === '/no-network') return;
      history.push('/no-network');
    };

    setNoNetworkNavigator(goNoNetwork);

    // Same client-side-navigation trick for the 503 / backend-degraded page.
    const goServiceUnavailable = () => {
      if (window.location.pathname === '/service-unavailable') return;
      history.push('/service-unavailable');
    };
    setServiceUnavailableNavigator(goServiceUnavailable);

    // Same client-side-navigation trick for the blocked-account wall, pushed
    // from the status check at the end of whoAmI().
    const goAccountStatus = () => {
      if (window.location.pathname === ACCOUNT_STATUS_PATH) return;
      history.push(ACCOUNT_STATUS_PATH);
    };
    setAccountStatusNavigator(goAccountStatus);

    if (!navigator.onLine) goNoNetwork();
    window.addEventListener('offline', goNoNetwork);

    return () => {
      setNoNetworkNavigator(null);
      setServiceUnavailableNavigator(null);
      setAccountStatusNavigator(null);
      window.removeEventListener('offline', goNoNetwork);
    };
  }, [history]);
  return null;
}

function AppContent() {
  // Mirror the unread activity count onto the installed PWA's app-icon
  // badge. Foreground half only — the service worker handles the app-closed
  // case from the push payload's badgeCount.
  useAppBadge();

  useEffect(() => {
    const version = import.meta.env.VITE_VERSION;
    console.log(`Version: ${version}`);
    logInfo(`App loaded - Version: ${version}`);
  }, []);

  return (
    <div className="hp-desktop-frame">
    <IonApp>
      <Toaster position="top-center" containerClassName="hp-toaster" />
      <IonReactRouter>
        <NavigationTracker />
        <NoNetworkNavigatorRegistration />
        <SecretLoginHandler />
        <PushTapNavigator />
        <PwaUpdateInitializer />
        <VersionCheckRunner />
        <UpdateAvailableBanner />
        {/* Right-side profile navigation drawer. Mounted as a sibling of the
            #main-content page it targets so Ionic can pair them via
            contentId; opened from the TopBar avatar. Self-hides when logged
            out. */}
        <ProfileMenu />
        {/* Unauth counterpart to ProfileMenu — same #main-content end slot,
            opened from the TopBar hamburger. Only one of the two is mounted at
            a time (each self-hides in the other's auth state). */}
        <GuestMenu />
        <div className="ion-page" id="main-content">
        <RegistrationGate>
          {/* Inner Suspense boundary around the page <Switch>. i18n is
              configured with `useSuspense: true` and only preloads `common`
              + `errors` — every per-screen namespace lazy-loads on first
              `useTranslation('ns')` call. Without this boundary, those
              suspends bubble to the root Suspense in main.tsx, which would
              unmount the entire <AppContent> (re-running auth/WS/push/device
              bootstrap and flashing the screen). Scoping the boundary here
              keeps AppContent and the gates mounted — only the page content
              briefly shows the spinner while its namespace JSON loads. */}
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center bg-background">
                <IonSpinner name="crescent" />
              </div>
            }
          >
          <Switch>
            <Route exact path="/registration-required">
              <RegistrationRequiredPage />
            </Route>
            <Route exact path="/">
              <HomePage />
            </Route>
            <Route exact path="/share">
              <ShareRedirectPage />
            </Route>
            <Route exact path="/n/:ref">
              <NotificationRefPage />
            </Route>
            <Route exact path="/r/:ref">
              <CampaignLinkPage />
            </Route>
            {/* Connection deep link (`/c={id}`). Public so an unauthenticated
                recipient can sign in/up in place without losing the id. */}
            <Route exact path="/c=:id">
              <ConnectionResolvePage />
            </Route>
            <Route exact path="/update-required">
              <UpdateRequiredPage />
            </Route>
            <Route exact path="/no-network">
              <NoNetworkPage />
            </Route>
            <Route exact path="/service-unavailable">
              <ServiceUnavailableScreen />
            </Route>
            <Route exact path={ACCOUNT_STATUS_PATH}>
              <AccountStatusPage />
            </Route>
            <Route exact path="/listing/:id">
              <ListingDetailsPage />
            </Route>
            {/* Public: logged-out visitors can build a dish here and are
                prompted to create an account at the final Publish step (the
                page buffers the in-progress dish client-side until then). */}
            <Route exact path="/publish-listing/:id?">
              <PublishListingPage />
            </Route>
            <Route exact path="/orders/:id/checkout">
              <ProtectedRoute>
                <OrderCheckoutPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/orders/:id">
              <ProtectedRoute>
                <OrderInProgressPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/profile">
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/settings">
              <ProtectedRoute>
                <SettingsNotificationsPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/connections">
              <ProtectedRoute>
                <ConnectionsVisibilityPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/my-kitchen">
              <ProtectedRoute>
                <MyKitchenPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/my-earnings">
              <ProtectedRoute>
                <MyEarningsPage />
              </ProtectedRoute>
            </Route>
            <Route exact path="/my-orders">
              <ProtectedRoute>
                <MyOrdersPage />
              </ProtectedRoute>
            </Route>
            {/* Catch-all: any unmatched URL (e.g. a stale `/sign-in` link, a
                mistyped path) bounces to home instead of rendering a blank
                page — the Switch otherwise matches nothing and renders null.
                Must stay last so every real route above wins first. */}
            <Route>
              <Redirect to="/" />
            </Route>
          </Switch>
          </Suspense>
        </RegistrationGate>
        </div>
        <GlobalErrorModal />
        <ExpiredLinkModal />
        <PushBlockedModal />
        <PushPermissionModal />
        <PushFirstLaunchTrigger />
        <InstallInstructionsModal />
        <ActivitySheetMount />
        {/* Handoff block: an eater whose chef has confirmed handoff can't use
            the app until they confirm their own half. Mounted at the root (not
            on the order page) because the point is that navigating away is not
            an escape. Last in the stack, above the other global modals. */}
        <OrderConfirmGate />
      </IonReactRouter>
    </IonApp>
    {/* QR companion renders on staging + prod. Each env has its own PNG so
     * the encoded URL points at the matching app domain (no shared link that
     * would cross environments). Dev/local intentionally skip — the QR
     * would just encode a localhost URL that's useless on another device. */}
    {(import.meta.env.MODE === 'stg' || import.meta.env.MODE === 'prod') && (
      <aside className="hp-frame-companion" aria-hidden="true">
        <p className="hp-frame-companion__lead">
          The app works here, and...
          <br />
          <em>it works best on mobile.</em>
        </p>
        <img
          src={import.meta.env.MODE === 'prod' ? '/images/qr-app-prod.png' : '/images/qr-app-stg.png'}
          alt=""
          className="hp-frame-companion__qr"
        />
        <p className="hp-frame-companion__hint">
          Just scan this code<br />with your phone.
        </p>
      </aside>
    )}
    {/* LOCAL and DEV-ONLY text-scale simulator — remove when responsive-text work lands. */}
    {(import.meta.env.DEV || import.meta.env.MODE === 'dev') && <TextScaleDevTool />}
    </div>
  );
}

// 404 = the resource genuinely doesn't exist for the caller (locale not a
// dish locale, listing deleted, etc.). Retrying just pays N× round-trips
// to land on the same answer while the user stares at a spinner. TanStack
// Query's transport-agnostic default of 3 retries can't tell HTTP semantics
// from a generic thrown error; we know our apiService attaches `.status` on
// every HTTP failure, so we can short-circuit definitively here. Other
// errors (network, 5xx, no `.status`) keep the default 3 retries because
// they're typically transient.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        if ((err as Error & { status?: number } | undefined)?.status === 404) return false;
        return failureCount < 3;
      },
    },
  },
});
setWsQueryClient(queryClient);

const YBUG_ID = import.meta.env.VITE_YBUG_ID;
const ybugEnabled =
  import.meta.env.VITE_SHOW_YBUG === 'true' && Boolean(YBUG_ID);

declare global {
  interface Window {
    __hpBootDone?: () => void;
  }
}

// Tag <html> with a class for any iOS-specific styling we add to global.scss
// in the future. Currently no Ybug rules use it (Ybug renders inside an
// iframe and our CSS doesn't cascade into it), but the tag is cheap. The
// Ybug-iOS workarounds themselves live in services/ybugSettings.ts and
// are wired in via both YbugProvider's settings prop AND ybug.init() in
// InitYbug — ybug.init() REPLACES window.ybug_settings, so flow directives
// like skip_to need to be present in BOTH paths to survive auth.
if (isIOS && typeof document !== 'undefined') {
  document.documentElement.classList.add('hp-ios');
}

const App: React.FC = () => {
  // Clear the boot watchdog (set in index.html) — if we got here, React
  // mounted and the silent-white-screen fallback is no longer needed.
  useEffect(() => {
    window.__hpBootDone?.();
  }, []);

  const inner = (
    <>
      <InitDevice />
      <InitUtm />
      <InitLocale />
      <DeviceLocationGuard />
      <InitAuth />
      <InitWs />
      <InitPush />
      <AppContent />
    </>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {ybugEnabled ? (
        <YbugProvider ybugId={YBUG_ID} settings={ybugSettings}>
          <InitYbug />
          {inner}
        </YbugProvider>
      ) : (
        inner
      )}
    </QueryClientProvider>
  );
};

export default App;
